/**
 * Organization Role Definitions
 *
 * Defines typed roles for enterprise organization membership.
 * These roles determine what actions users can perform on organizations.
 */

export enum OrganizationRole {
  /**
   * OWNER - Full management access to the organization including billing,
   * user management, and all organization resources.
   */
  OWNER = "OWNER",

  /**
   * ADMIN - Administrative access to manage organization resources and settings,
   * but may have limited billing access compared to OWNER.
   */
  ADMIN = "ADMIN",

  /**
   * VIEWER - Read-only access to organization details and resources.
   */
  VIEWER = "VIEWER"
}

/**
 * Check if a role has administrative privileges (OWNER or ADMIN).
 *
 * @param role - The organization role to check
 * @returns true if the role is OWNER or ADMIN, false otherwise
 */
export function isOrganizationAdmin(role: OrganizationRole): boolean {
  return role === OrganizationRole.OWNER || role === OrganizationRole.ADMIN
}

/**
 * Parse a string role value to OrganizationRole enum.
 * Returns undefined if the string doesn't match a valid role.
 *
 * @param role - String role value from database or API
 * @returns OrganizationRole enum or undefined if invalid
 */
export function parseOrganizationRole(role: string): OrganizationRole | undefined {
  if (Object.values(OrganizationRole).includes(role as OrganizationRole)) {
    return role as OrganizationRole
  }
  return undefined
}
